import { useState, useEffect } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
} from "@mui/material";
import dayjs from "dayjs";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import ActionsService from "../../services/actions.service";

const GoogleCalendarDialog = ({ open, onClose, onDisconnect }) => {
  const [events, setEvents] = useState([]);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [formData, setFormData] = useState({
    id: null,
    title: "",
    start: dayjs(),
    end: dayjs().add(1, "hour"),
  });

  // 🔄 Fetch events from Google Calendar when dialog opens
  useEffect(() => {
    if (open) {
      loadEvents();
    }
  }, [open]);

  const loadEvents = async () => {
    try {
      const result = await ActionsService.listEvents({
        timeMin: dayjs().startOf("month").toISOString(),
        timeMax: dayjs().endOf("month").toISOString(),
      });
      if (result) {
        setEvents(
          result.map((evt) => ({
            id: evt.id,
            title: evt.summary,
            start: evt.start.dateTime || evt.start.date,
            end: evt.end.dateTime || evt.end.date,
          }))
        );
      }
    } catch (err) {
      console.error("Failed to load events", err);
    }
  };

  // 📌 Create new event from date click
  const handleDateClick = (info) => {
    setIsEditing(false);
    setFormData({
      id: null,
      title: "",
      start: dayjs(info.date).hour(9),
      end: dayjs(info.date).hour(10),
    });
    setEventDialogOpen(true);
  };

  // 📌 Open existing event
  const handleEventClick = (info) => {
    const event = info.event;
    setIsEditing(true);
    setFormData({
      id: event.id,
      title: event.title,
      start: dayjs(event.start),
      end: event.end ? dayjs(event.end) : dayjs(event.start).add(1, "hour"),
    });
    setEventDialogOpen(true);
  };

  const handleInputChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // 📌 Save (create or update)
  const handleSave = async () => {
    try {
      if (isEditing) {
        await ActionsService.updateEvent({
          eventId: formData.id,
          summary: formData.title,
          start: { dateTime: formData.start.toISOString() },
          end: { dateTime: formData.end.toISOString() },
        });
      } else {
        await ActionsService.createEvent({
          summary: formData.title,
          start: { dateTime: formData.start.toISOString() },
          end: { dateTime: formData.end.toISOString() },
        });
      }
      await loadEvents();
      setEventDialogOpen(false);
    } catch (err) {
      console.error("Failed to save event", err);
    }
  };

  // 📌 Delete event
  const handleDelete = async () => {
    try {
      await ActionsService.deleteEvent({ eventId: formData.id });
      await loadEvents();
      setEventDialogOpen(false);
    } catch (err) {
      console.error("Failed to delete event", err);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Google Calendar</DialogTitle>

      <DialogContent dividers>
        <Box sx={{ height: "70vh" }}>
          <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            selectable
            events={events}
            dateClick={handleDateClick}
            eventClick={handleEventClick}
            height="100%"
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button color="error" onClick={onDisconnect}>
          Disconnect
        </Button>
      </DialogActions>

      {/* Inner event create/edit dialog */}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Dialog
          open={eventDialogOpen}
          onClose={() => setEventDialogOpen(false)}
        >
          <DialogTitle>{isEditing ? "Edit Event" : "Create Event"}</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Event Title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              margin="dense"
            />

            <DateTimePicker
              label="Start"
              value={formData.start}
              onChange={(newValue) =>
                setFormData((prev) => ({ ...prev, start: newValue }))
              }
              slotProps={{ textField: { fullWidth: true, margin: "dense" } }}
            />

            <DateTimePicker
              label="End"
              value={formData.end}
              onChange={(newValue) =>
                setFormData((prev) => ({ ...prev, end: newValue }))
              }
              slotProps={{ textField: { fullWidth: true, margin: "dense" } }}
            />
          </DialogContent>
          <DialogActions>
            {isEditing && (
              <Button color="error" onClick={handleDelete}>
                Delete
              </Button>
            )}
            <Button onClick={() => setEventDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!formData.title.trim()}
            >
              {isEditing ? "Update" : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      </LocalizationProvider>
    </Dialog>
  );
};

export default GoogleCalendarDialog;
